import type {
  HsAttributeView,
  HsBoundingBoxView,
  HsDocumentView,
  HsFrustumDataView,
  HsFrustumObjectInfo,
  HsFrustumRequestInfo,
  HsFrustumView,
  HsGeometryComponentView,
  HsHlodTilesetLink,
  HsMaterialView,
  HsModelSceneParticipationView,
  HsModelView,
  HsNodeAttributeInfo,
  HsNodeCreateInfo,
  HsNodeSmallView,
  HsNodesVisibilityView,
  HsNodeTransformView,
  HsNodeView,
  HsOriginalMeshSource,
  HsProcessingView,
  HsSceneGeometryReuseView,
  HsSceneModelInstanceInfo,
  HsSceneModelInstanceView,
  HsSceneNodeFilterResultView,
  HsSceneSettings,
  HsSceneView,
  HsSimilarityRunView,
  HsTransformInfo,
  HsTransformVectorView,
  HsTransportResponse,
  HsVector3View,
  HsVertexColorGroupView,
} from '@sibe/hyperscene';
import { HsFormatRegistry } from '@lib/core-ts/viewer/extensions/format-registry.class';
import { hsEnsureOk } from '@lib/shared/http/api-error.function';
import { HsCatalogApi } from '@sibe/hyperscene/api/catalog';
import { HsIngestApi } from '@sibe/hyperscene/api/ingest';
import { HsViewerApi } from '@sibe/hyperscene/api/viewer';
import { HsMaterialsFormat } from '@sibe/hyperscene/formats/materials';
import { HsHttpTransport } from '@sibe/hyperscene/transports/http';
import {
  ViewerFrustumResponseFormat,
  ViewerGeometryFormat,
  ViewerPointCloudGeometryFormat,
} from '../models/viewer-runtime-settings';
import {
  HS_FRUSTUM_JSON_CONTENT_TYPE,
  HS_FRUSTUM_V4_CONTENT_TYPE,
  HsFrustumFormat,
} from '@sibe/hyperscene/formats/frustum';

export type HypersceneDocumentView = HsDocumentView;

export type HypersceneProcessingView = HsProcessingView;

export type HypersceneModelView = HsModelView;

export type HypersceneModelSceneParticipationView = HsModelSceneParticipationView;

export type HypersceneSimilarityRunView = HsSimilarityRunView;

export type HypersceneNodeView = HsNodeView;

export type HypersceneNodeSmallView = HsNodeSmallView;

export type HypersceneAttributeView = HsAttributeView;

export type HypersceneSceneView = HsSceneView;
export type HypersceneSceneSettings = HsSceneSettings;

export type HypersceneSceneGeometryReuseView = HsSceneGeometryReuseView;

export type HypersceneMaterialView = HsMaterialView;

export type HypersceneVertexColorGroupView = HsVertexColorGroupView;

export type HypersceneNodesVisibilityView = HsNodesVisibilityView;

export type HypersceneSceneNodeFilterResultView = HsSceneNodeFilterResultView;

export type HypersceneTransformVectorView = HsTransformVectorView;

export type HypersceneSceneModelInstanceInfo = HsSceneModelInstanceInfo;

export type HypersceneSceneModelInstanceView = HsSceneModelInstanceView;

export type HypersceneNodeTransformView = HsNodeTransformView;

export type HypersceneBoundingBoxView = HsBoundingBoxView;

export type HypersceneVector3Dto = HsVector3View;

export type HypersceneFrustumObjectInfo = HsFrustumObjectInfo;

export type HypersceneFrustumRequestInfo = HsFrustumRequestInfo;

export type HypersceneFrustumDataView = HsFrustumDataView;

export type HypersceneFrustumView = HsFrustumView;

export type HypersceneGeometryComponentView = HsGeometryComponentView;

export type HypersceneOriginalMeshSource = HsOriginalMeshSource;

export type HypersceneNodeAttributeInfo = HsNodeAttributeInfo;

export type HypersceneNodeCreateInfo = HsNodeCreateInfo;

interface ApiClients {
  transport: HsHttpTransport;
  viewer: HsViewerApi;
  catalog: HsCatalogApi;
  ingest: HsIngestApi;
}

export class HypersceneIngestAdapterService {
  private readonly formats: HsFormatRegistry = makeViewerFormats();
  private readonly apiClients: Map<string, ApiClients> = new Map();

  public createIfcMergeScript(): Blob {
    const script: string = `#!/usr/bin/env python3
"""Merge IFC files with per-file 4x4 transformation matrices.

Matrix translation values are specified in SI metres. They are converted to
the native length units of each input IFC file before placement editing.

Install the dependency once with:
  python3 -m pip install ifcopenshell

Example:
  python3 hyperscene-ifc-merge.py \\
    --file "building-a.ifc" \\
    --matrix "1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1" \\
    --file "building-b.ifc" \\
    --matrix "1,0,0,10,0,1,0,20,0,0,1,30,0,0,0,1" \\
    --output "merged.ifc"
"""

import argparse
from concurrent.futures import ProcessPoolExecutor
from pathlib import Path
import re
from tempfile import TemporaryDirectory

try:
  import ifcopenshell
  import ifcopenshell.api
  from ifcopenshell.util.schema import Migrator
  from ifcopenshell.util.unit import calculate_unit_scale
  from ifcopenshell.util.placement import get_local_placement
except ImportError as error:
  raise SystemExit(
    "Missing dependency. Install it with: python3 -m pip install ifcopenshell"
  ) from error


def log(message: str) -> None:
  print(f"[ifc-merge] {message}", flush=True)


def parse_arguments() -> argparse.Namespace:
  parser = argparse.ArgumentParser(description=__doc__)
  parser.add_argument(
    "--file",
    dest="files",
    action="append",
    required=True,
    help="IFC input file. Repeat once for every model.",
  )
  parser.add_argument(
    "--matrix",
    dest="matrices",
    action="append",
    required=True,
    help="16 comma-separated matrix values in row-major order. Repeat per file.",
  )
  parser.add_argument(
    "--schema",
    help="Target IFC schema. If omitted, schemas are preserved unless mixed inputs require confirmation.",
  )
  parser.add_argument(
    "--workers",
    type=int,
    default=1,
    help="Process count for independent input processing and graph assembly. Final stitching remains sequential.",
  )
  parser.add_argument("--output", default="merged.ifc", help="Output IFC filename.")
  return parser.parse_args()


def parse_matrix(value: str):
  values = [float(part.strip()) for part in value.split(",") if part.strip()]
  if len(values) != 16:
    raise ValueError("Each --matrix argument must contain exactly 16 comma-separated values.")
  return [values[row * 4:(row + 1) * 4] for row in range(4)]


def multiply_matrices(left, right):
  return [
    [sum(left[row][index] * right[index][column] for index in range(4)) for column in range(4)]
    for row in range(4)
  ]


def is_identity_matrix(matrix) -> bool:
  identity = (
    (1, 0, 0, 0),
    (0, 1, 0, 0),
    (0, 0, 1, 0),
    (0, 0, 0, 1),
  )
  return all(
    abs(matrix[row][column] - identity[row][column]) <= 1e-12
    for row in range(4)
    for column in range(4)
  )


def matrix_in_model_units(matrix, unit_scale: float):
  if unit_scale <= 0:
    raise ValueError(f"IFC unit scale must be positive, got {unit_scale}.")
  result = [row[:] for row in matrix]
  translation_scale = 1 / unit_scale
  result[0][3] *= translation_scale
  result[1][3] *= translation_scale
  result[2][3] *= translation_scale
  return result


def resolve_path(value: str) -> Path:
  path = Path(value).expanduser()
  return path if path.is_absolute() else Path(__file__).resolve().parent / path


def read_schema(source_path: Path) -> str:
  with source_path.open("rb") as stream:
    header = stream.read(8192).decode("ascii", errors="ignore")
  match = re.search(r"FILE_SCHEMA\\s*\\(\\s*\\(\\s*'([^']+)'", header, re.IGNORECASE)
  if match is None:
    raise RuntimeError(f"Could not determine IFC schema from '{source_path}'")
  return match.group(1).upper()


def confirm_schema_conversion(schemas) -> bool:
  schema_list = ", ".join(schemas)
  log(f"WARNING: input files use different IFC schemas: {schema_list}")
  try:
    answer = input("Convert all input files to IFC4 and continue? [Y/N]: ").strip().upper()
  except EOFError:
    answer = "N"
  return answer == "Y"


def migrate_to_schema(source, target_schema: str, source_path: Path):
  log(f"Converting '{source_path.name}' from {source.schema} to {target_schema}")
  migrated = ifcopenshell.file(schema=target_schema)
  migrator = Migrator()
  migrator.preprocess(source, migrated)
  for entity in source:
    migrator.migrate(entity, migrated)
  if migrated.schema != target_schema:
    raise RuntimeError(
      f"Schema conversion failed for {source_path.name}: "
      f"created {migrated.schema}, expected {target_schema}"
    )
  log(f"Converted '{source_path.name}' to {migrated.schema}")
  return migrated


def apply_matrix_to_geometry(model, matrix) -> int:
  """Apply a matrix to top-level products and let their children inherit it."""
  if is_identity_matrix(matrix):
    log("Identity matrix; skipped placement edits")
    return 0

  unit_scale = calculate_unit_scale(model)
  log(f"Input IFC length unit scale: {unit_scale}; matrix translations are SI metres")
  model_matrix = matrix_in_model_units(matrix, unit_scale)
  transformed_count = 0
  products = model.by_type("IfcProduct")
  for product in products:
    placement = getattr(product, "ObjectPlacement", None)
    if placement is None or getattr(placement, "PlacementRelTo", None) is not None:
      continue
    existing_matrix = get_local_placement(placement).tolist()
    composed_matrix = multiply_matrices(model_matrix, existing_matrix)
    ifcopenshell.api.run(
      "geometry.edit_object_placement",
      model,
      product=product,
      matrix=composed_matrix,
      is_si=False,
      should_transform_children=True,
    )
    transformed_count += 1

  return transformed_count


def regenerate_global_ids(source):
  for entity in source.by_type("IfcRoot"):
    if getattr(entity, "GlobalId", None) is not None:
      entity.GlobalId = ifcopenshell.guid.new()


def replace_project_reference(value, source_project, target_project):
  if isinstance(value, ifcopenshell.entity_instance):
    if value.id() == source_project.id():
      return target_project, True
    return value, False
  if isinstance(value, tuple):
    replaced_items = []
    changed = False
    for item in value:
      replacement, item_changed = replace_project_reference(item, source_project, target_project)
      replaced_items.append(replacement)
      changed = changed or item_changed
    return (tuple(replaced_items) if changed else value), changed
  if isinstance(value, list):
    replaced_items = []
    changed = False
    for item in value:
      replacement, item_changed = replace_project_reference(item, source_project, target_project)
      replaced_items.append(replacement)
      changed = changed or item_changed
    return (replaced_items if changed else value), changed
  return value, False


def assemble_source_graph(source, target, target_project=None):
  """Attach a complete source graph using IfcOpenShell's C++ entity transfer."""
  projects = source.by_type("IfcProject")
  if len(projects) != 1:
    raise RuntimeError(
      f"Expected exactly one IfcProject in source, found {len(projects)}"
    )

  source_project = projects[0]
  source_entities = list(source)
  if target_project is None:
    target.wrapped_data.addEntities([entity.wrapped_data for entity in source_entities])
    return target.by_type("IfcProject")[0], len(source_entities) - 1

  for entity in source.get_inverse(source_project):
    for index, value in enumerate(entity):
      replacement, changed = replace_project_reference(value, source_project, target_project)
      if changed:
        entity[index] = replacement
  target.wrapped_data.addEntities(
    [entity.wrapped_data for entity in source_entities if entity.id() != source_project.id()]
  )
  return target_project, len(source_entities) - 1


def assemble_preprocessed_source(source, target, target_project=None):
  target_project, _copied_count = assemble_source_graph(source, target, target_project)
  return target_project


def assemble_input(task):
  source_path, matrix, target_schema, output_path = task
  source = ifcopenshell.open(str(source_path))
  if source.schema != target_schema:
    source = migrate_to_schema(source, target_schema, Path(source_path))
  transformed_count = apply_matrix_to_geometry(source, matrix)
  assembled = ifcopenshell.file(schema=target_schema)
  source_entities = list(source)
  regenerate_global_ids(source)
  _target_project, copied_count = assemble_source_graph(source, assembled)
  assembled.write(str(output_path))
  return transformed_count, copied_count


def validate_merged_model(model) -> None:
  projects = model.by_type("IfcProject")
  if len(projects) != 1:
    raise RuntimeError(
      f"Merged model must contain exactly one IfcProject, found {len(projects)}"
    )

  global_ids = {}
  duplicates = []
  for entity in model.by_type("IfcRoot"):
    value = getattr(entity, "GlobalId", None)
    if value is None:
      continue
    previous = global_ids.get(value)
    if previous is not None:
      duplicates.append((value, previous, entity.is_a()))
    else:
      global_ids[value] = entity.is_a()

  if duplicates:
    sample = ", ".join(item[0] for item in duplicates[:5])
    raise RuntimeError(f"Merged model contains duplicate GlobalId values: {sample}")


def main() -> None:
  arguments = parse_arguments()
  if len(arguments.files) < 2:
    raise SystemExit("At least two --file arguments are required for a merge.")
  if len(arguments.files) != len(arguments.matrices):
    raise SystemExit("Provide exactly one --matrix argument for every --file argument.")
  if arguments.workers < 1:
    raise SystemExit("--workers must be at least 1.")

  inputs = list(zip(arguments.files, (parse_matrix(value) for value in arguments.matrices)))
  input_specs = []
  for index, (file_name, matrix) in enumerate(inputs, start=1):
    source_path = resolve_path(file_name)
    schema = read_schema(source_path)
    log(f"[{index}/{len(inputs)}] Found schema {schema} in '{source_path}'")
    input_specs.append((source_path, matrix, schema))

  schemas = list(dict.fromkeys(schema for _source_path, _matrix, schema in input_specs))
  requested_schema = arguments.schema.upper() if arguments.schema else None
  if requested_schema is not None and requested_schema not in ("IFC2X3", "IFC4"):
    raise SystemExit("Unsupported target schema. Use IFC2X3 or IFC4.")

  if requested_schema is not None:
    target_schema = requested_schema
    log(f"Using target schema {target_schema} from --schema")
  elif len(schemas) == 1:
    target_schema = schemas[0]
    log(f"All input files use {target_schema}; no schema conversion requested")
  elif confirm_schema_conversion(schemas):
    target_schema = "IFC4"
    log("User selected Y; converting all input files to IFC4")
  else:
    raise SystemExit("Merge canceled. Input files use different schemas and no conversion was selected.")

  log(
    f"Starting merge: {len(input_specs)} input file(s), target schema {target_schema}, "
    f"output '{arguments.output}'"
  )
  merged = ifcopenshell.file(schema=target_schema)
  target_project = None

  if arguments.workers == 1:
    for index, (source_path, matrix, _schema) in enumerate(input_specs, start=1):
      log(f"[{index}/{len(inputs)}] Loading '{source_path}'")
      source = ifcopenshell.open(str(source_path))
      if source.schema != target_schema:
        source = migrate_to_schema(source, target_schema, source_path)
      transformed_count = apply_matrix_to_geometry(source, matrix)
      regenerate_global_ids(source)
      target_project, copied_count = assemble_source_graph(source, merged, target_project)
      log(
        f"[{index}/{len(inputs)}] Applied matrix to {transformed_count} root placement(s); "
        f"copied {copied_count} entities"
      )
  else:
    worker_count = min(arguments.workers, len(input_specs))
    log(f"Processing inputs and assembling graphs with {worker_count} worker process(es)")
    with TemporaryDirectory(prefix="ifc-merge-") as temporary_directory:
      tasks = [
        (source_path, matrix, target_schema, Path(temporary_directory) / f"input-{index}.ifc")
        for index, (source_path, matrix, _schema) in enumerate(input_specs, start=1)
      ]
      with ProcessPoolExecutor(max_workers=worker_count) as executor:
        processing_results = list(executor.map(assemble_input, tasks))

      for index, (task, (transformed_count, copied_count)) in enumerate(zip(tasks, processing_results), start=1):
        normalized_path = task[3]
        source = ifcopenshell.open(str(normalized_path))
        target_project = assemble_preprocessed_source(source, merged, target_project)
        log(
          f"[{index}/{len(inputs)}] Applied matrix to {transformed_count} root placement(s); "
          f"copied {copied_count} entities"
        )

  validate_merged_model(merged)
  log("Validated merged model: one IfcProject and unique GlobalId values")
  output_path = resolve_path(arguments.output)
  log(f"Writing merged model to '{output_path}'")
  merged.write(str(output_path))
  log(f"Merge complete: wrote '{output_path}'")


if __name__ == "__main__":
  main()
`;
    return new Blob([script], { type: 'text/x-python' });
  }

  public createIfcMergeCommand(
    items: readonly { fileName: string; matrix: readonly number[] }[],
    scriptFileName: string = 'hyperscene-ifc-merge.py',
    outputFileName: string = 'merged.ifc',
    targetSchema?: string,
    workers?: number
  ): string {
    const quoteShellArgument = (value: string): string => {
      const escapedValue: string = value.replace(/'/g, "'\\''");
      return `'${escapedValue}'`;
    };
    const lines: string[] = [`python3 ${quoteShellArgument(scriptFileName)}`];
    const commandArguments: string[] =
      targetSchema === undefined ? [] : [`--schema ${quoteShellArgument(targetSchema)}`];
    if (workers !== undefined) {
      const workerCount: number = Number.isFinite(workers) ? Math.max(1, Math.floor(workers)) : 1;
      commandArguments.push(`--workers ${workerCount}`);
    }
    commandArguments.push(
      ...items.flatMap((item: { fileName: string; matrix: readonly number[] }) => [
        `--file ${quoteShellArgument(item.fileName)}`,
        `--matrix ${quoteShellArgument(
          Array.from({ length: 16 }, (_value: undefined, index: number) => Number(item.matrix[index] ?? 0)).join(',')
        )}`,
      ])
    );

    if (items.length === 0) {
      lines.push(`  --help`);
      return lines.join(' \\\n');
    }

    commandArguments.push(`--output ${quoteShellArgument(outputFileName)}`);
    return [...lines, ...commandArguments.map((value: string) => `  ${value}`)].join(' \\\n');
  }

  public async uploadDocument(apiBaseUrl: string, file: File): Promise<HypersceneDocumentView> {
    return this.ingest(apiBaseUrl).uploadDocument(file, file.name);
  }

  public async parseDocument(apiBaseUrl: string, request: unknown): Promise<string> {
    return this.ingest(apiBaseUrl).parseDocument(request);
  }

  public async reparseModel(apiBaseUrl: string, modelId: string): Promise<string> {
    return this.ingest(apiBaseUrl).reparseModel(modelId);
  }

  public async rerunModelSimilarity(
    apiBaseUrl: string,
    modelId: string,
    restoreExistingReplacements: boolean = false
  ): Promise<string> {
    return this.catalog(apiBaseUrl).rerunModelSimilarity(modelId, { restoreExistingReplacements });
  }

  public async rerunSceneGeometryReuse(apiBaseUrl: string, sceneId: string): Promise<HypersceneSceneGeometryReuseView> {
    return this.catalog(apiBaseUrl).rerunSceneGeometryReuse(sceneId);
  }

  public async getModelSimilarityRuns(apiBaseUrl: string, modelId: string): Promise<HypersceneSimilarityRunView[]> {
    return this.catalog(apiBaseUrl).getModelSimilarityRuns(modelId);
  }

  public async getProcessing(apiBaseUrl: string, processingId: string): Promise<HypersceneProcessingView> {
    return this.ingest(apiBaseUrl).getProcessing(processingId);
  }

  public async filterModels(apiBaseUrl: string, metadataInfos: unknown[]): Promise<HypersceneModelView[]> {
    return this.catalog(apiBaseUrl).filterModels(metadataInfos);
  }

  public async getRootNode(apiBaseUrl: string, modelId: string): Promise<HypersceneNodeView> {
    return this.catalog(apiBaseUrl).getModelRootNode(modelId);
  }

  public async getModelScenes(apiBaseUrl: string, modelId: string): Promise<HypersceneModelSceneParticipationView[]> {
    return this.catalog(apiBaseUrl).getModelScenes(modelId);
  }

  public async deleteModel(apiBaseUrl: string, modelId: string): Promise<void> {
    return this.catalog(apiBaseUrl).deleteModel(modelId);
  }

  public async putModelMetadata(
    apiBaseUrl: string,
    modelId: string,
    metadataInfos: unknown[]
  ): Promise<HypersceneModelView> {
    return this.catalog(apiBaseUrl).putModelMetadata(modelId, metadataInfos);
  }

  public async createScene(apiBaseUrl: string, metadataInfos: unknown[]): Promise<HypersceneSceneView> {
    return this.catalog(apiBaseUrl).createScene(metadataInfos);
  }

  public async filterScenes(apiBaseUrl: string, metadataInfos: unknown[]): Promise<HypersceneSceneView[]> {
    return this.catalog(apiBaseUrl).filterScenes(metadataInfos);
  }

  public async getScene(apiBaseUrl: string, sceneId: string): Promise<HypersceneSceneView> {
    return this.viewer(apiBaseUrl).getScene(sceneId);
  }

  public async getSceneBoundingBox(
    apiBaseUrl: string,
    sceneId: string
  ): Promise<{ value: { min: number[]; max: number[] } | null }> {
    return { value: await this.viewer(apiBaseUrl).getSceneBoundingBox(sceneId) };
  }

  public async getNodesBoundingBox(
    apiBaseUrl: string,
    nodeIds: string[]
  ): Promise<{ value: HypersceneBoundingBoxView | null }> {
    return { value: await this.viewer(apiBaseUrl).getNodesBoundingBox(nodeIds) };
  }

  public async putSceneMetadata(
    apiBaseUrl: string,
    sceneId: string,
    metadataInfos: unknown[]
  ): Promise<HypersceneSceneView> {
    return this.catalog(apiBaseUrl).putSceneMetadata(sceneId, metadataInfos);
  }

  public async putSceneSettings(
    apiBaseUrl: string,
    sceneId: string,
    settings: HypersceneSceneSettings
  ): Promise<HypersceneSceneView> {
    return this.catalog(apiBaseUrl).putSceneSettings(sceneId, settings);
  }

  public async deleteScene(apiBaseUrl: string, sceneId: string): Promise<void> {
    return this.catalog(apiBaseUrl).deleteScene(sceneId);
  }

  public async filterProcessings(apiBaseUrl: string, metadataInfos: unknown[]): Promise<HypersceneProcessingView[]> {
    return this.ingest(apiBaseUrl).filterProcessings(metadataInfos);
  }

  public async deleteProcessings(apiBaseUrl: string, processingIds: string[]): Promise<void> {
    return this.ingest(apiBaseUrl).deleteProcessings(processingIds);
  }

  public async getSceneRootNodes(
    apiBaseUrl: string,
    sceneId: string,
    nodeTags: string[] = []
  ): Promise<HypersceneNodeSmallView[]> {
    return this.viewer(apiBaseUrl).getSceneRootNodes(sceneId, nodeTags);
  }

  public async getNodesTreeOriginalMaterials(
    apiBaseUrl: string,
    rootNodeId: string
  ): Promise<HypersceneMaterialView[]> {
    return this.viewer(apiBaseUrl).getNodeTreeOriginalMaterials(rootNodeId);
  }

  public async getMaterialsByIds(apiBaseUrl: string, materialIds: string[]): Promise<HypersceneMaterialView[]> {
    return this.viewer(apiBaseUrl).getMaterials(materialIds);
  }

  public async getTexture(apiBaseUrl: string, textureId: string): Promise<Blob> {
    return this.viewer(apiBaseUrl).getDocument(textureId);
  }

  public async getScenePhotogrammetry(apiBaseUrl: string, sceneId: string): Promise<boolean> {
    return this.viewer(apiBaseUrl).hasPhotogrammetry(sceneId);
  }

  public async getPhotogrammetryProgressiveMeshes(apiBaseUrl: string, meshIds: string[]): Promise<HsHlodTilesetLink[]> {
    return this.viewer(apiBaseUrl).getPhotogrammetryTilesets(meshIds);
  }

  public async getTextures(apiBaseUrl: string, textureIds: string[]): Promise<Map<string, Blob>> {
    return this.viewer(apiBaseUrl).getDocuments(textureIds);
  }

  public async createNode(apiBaseUrl: string, nodeInfo: HypersceneNodeCreateInfo): Promise<HypersceneNodeView> {
    return this.catalog(apiBaseUrl).createNode(nodeInfo);
  }

  public async deleteNode(apiBaseUrl: string, nodeId: string): Promise<void> {
    return this.catalog(apiBaseUrl).deleteNode(nodeId);
  }

  public async getNodeTransform(apiBaseUrl: string, nodeId: string): Promise<HypersceneNodeTransformView> {
    return this.catalog(apiBaseUrl).getNodeTransform(nodeId);
  }

  public async transformNodes(
    apiBaseUrl: string,
    transforms: { nodeId: string; transformInfo: HsTransformInfo }[]
  ): Promise<void> {
    return this.catalog(apiBaseUrl).transformNodes(transforms);
  }

  public async getNodeGeometryIds(apiBaseUrl: string, nodeId: string): Promise<string[]> {
    return this.viewer(apiBaseUrl).getNodeGeometryIds(nodeId);
  }

  public async getNodeGeometryIdsByFilter(
    apiBaseUrl: string,
    nodeId: string,
    filter: 'Meshes' | 'Lines' | 'PointClouds'
  ): Promise<string[]> {
    return this.viewer(apiBaseUrl).getNodeGeometryIds(nodeId, filter);
  }

  public async getNodesTreeGeometriesByFilter(
    apiBaseUrl: string,
    nodeId: string,
    filter: 'Meshes' | 'Lines' | 'PointClouds'
  ): Promise<HypersceneGeometryComponentView[]> {
    return this.viewer(apiBaseUrl).getNodeTreeGeometries(nodeId, filter);
  }

  public async getMeshesBinarySize(
    apiBaseUrl: string,
    geometryIds: string[],
    format: ViewerGeometryFormat = 'binary'
  ): Promise<number> {
    return (await this.viewer(apiBaseUrl).getGeometryPayload('Meshes', geometryIds, { format })).byteLength;
  }

  public async getOriginalMeshSources(apiBaseUrl: string, nodeIds: string[]): Promise<HypersceneOriginalMeshSource[]> {
    return this.viewer(apiBaseUrl).getOriginalMeshSources(nodeIds);
  }

  public async getMeshesBinary(
    apiBaseUrl: string,
    geometryIds: string[],
    signal?: AbortSignal,
    format: ViewerGeometryFormat = 'binary',
    _canonicalGeometryIds: string[] = []
  ): Promise<ArrayBuffer> {
    return this.viewer(apiBaseUrl).getGeometryPayload('Meshes', geometryIds, { format, signal });
  }

  public async getLinesBinarySize(apiBaseUrl: string, geometryIds: string[]): Promise<number> {
    return (await this.viewer(apiBaseUrl).getGeometryPayload('Lines', geometryIds)).byteLength;
  }

  public async getLinesBinary(apiBaseUrl: string, geometryIds: string[], signal?: AbortSignal): Promise<ArrayBuffer> {
    return this.viewer(apiBaseUrl).getGeometryPayload('Lines', geometryIds, { signal });
  }

  public async getPointCloudsBinarySize(
    apiBaseUrl: string,
    geometryIds: string[],
    format: ViewerPointCloudGeometryFormat = 'binary'
  ): Promise<number> {
    return (await this.viewer(apiBaseUrl).getGeometryPayload('PointClouds', geometryIds, { format })).byteLength;
  }

  public async getPointCloudsBinary(
    apiBaseUrl: string,
    geometryIds: string[],
    signal?: AbortSignal,
    format: ViewerPointCloudGeometryFormat = 'binary'
  ): Promise<ArrayBuffer> {
    return this.viewer(apiBaseUrl).getGeometryPayload('PointClouds', geometryIds, { format, signal });
  }

  public async putNodeTreeToScene(apiBaseUrl: string, sceneId: string, parentNodeId: string): Promise<void> {
    return this.catalog(apiBaseUrl).addNodeTreeToScene(sceneId, parentNodeId);
  }

  public async createSceneModelInstance(
    apiBaseUrl: string,
    sceneId: string,
    info: HypersceneSceneModelInstanceInfo
  ): Promise<HypersceneNodeView> {
    return this.catalog(apiBaseUrl).createSceneModelInstance(sceneId, info);
  }

  public async getSceneModelInstance(
    apiBaseUrl: string,
    sceneId: string,
    rootNodeId: string
  ): Promise<HypersceneSceneModelInstanceView | null> {
    return this.catalog(apiBaseUrl).getSceneModelInstance(sceneId, rootNodeId);
  }

  public async transformSceneModelInstance(
    apiBaseUrl: string,
    sceneId: string,
    rootNodeId: string,
    transformInfo: HypersceneSceneModelInstanceInfo['transformInfo']
  ): Promise<HypersceneNodeView> {
    return this.catalog(apiBaseUrl).transformSceneModelInstance(sceneId, rootNodeId, transformInfo);
  }

  public async removeNodesTreesFromScene(
    apiBaseUrl: string,
    sceneId: string,
    parentNodeIds: string[],
    force: boolean = false
  ): Promise<void> {
    return this.catalog(apiBaseUrl).removeNodeTreesFromScene(sceneId, parentNodeIds, force);
  }

  public async getNode(apiBaseUrl: string, nodeId: string): Promise<HypersceneNodeView> {
    return this.viewer(apiBaseUrl).getNode(nodeId);
  }

  public async getNodesAllChildren(apiBaseUrl: string, nodeIds: readonly string[]): Promise<HypersceneNodeSmallView[]> {
    return this.viewer(apiBaseUrl).getNodesAllChildren(nodeIds);
  }

  public async getNodeChildren(apiBaseUrl: string, nodeId: string): Promise<HypersceneNodeSmallView[]> {
    return this.viewer(apiBaseUrl).getNodeChildren(nodeId);
  }

  public async getNodeChildrenBulk(apiBaseUrl: string, nodeIds: string[]): Promise<HypersceneNodeSmallView[]> {
    return this.viewer(apiBaseUrl).getNodeChildrenBulk(nodeIds);
  }

  public async getNodeRoute(apiBaseUrl: string, nodeId: string): Promise<string[]> {
    return this.viewer(apiBaseUrl).getNodeRoute(nodeId);
  }

  // With a viewSessionId the visibility calls change and read that viewer's own visibility, not the scene's.

  public async hideSceneNodes(
    apiBaseUrl: string,
    sceneId: string,
    nodeIds: string[],
    viewSessionId?: string
  ): Promise<void> {
    const api: HsViewerApi = this.viewer(apiBaseUrl);
    return viewSessionId === undefined
      ? api.hideNodes(sceneId, nodeIds)
      : api.hideSessionNodes(sceneId, viewSessionId, nodeIds);
  }

  public async unhideSceneNodes(
    apiBaseUrl: string,
    sceneId: string,
    nodeIds: string[],
    viewSessionId?: string
  ): Promise<void> {
    const api: HsViewerApi = this.viewer(apiBaseUrl);
    return viewSessionId === undefined
      ? api.unhideNodes(sceneId, nodeIds)
      : api.unhideSessionNodes(sceneId, viewSessionId, nodeIds);
  }

  public async isolateSceneNodes(
    apiBaseUrl: string,
    sceneId: string,
    nodeIds: string[],
    viewSessionId?: string
  ): Promise<void> {
    const api: HsViewerApi = this.viewer(apiBaseUrl);
    return viewSessionId === undefined
      ? api.isolateNodes(sceneId, nodeIds)
      : api.isolateSessionNodes(sceneId, viewSessionId, nodeIds);
  }

  public async unhideAllSceneNodes(apiBaseUrl: string, sceneId: string, viewSessionId?: string): Promise<void> {
    const api: HsViewerApi = this.viewer(apiBaseUrl);
    return viewSessionId === undefined
      ? api.unhideAllNodes(sceneId)
      : api.unhideAllSessionNodes(sceneId, viewSessionId);
  }

  public async getSceneNodesVisibility(
    apiBaseUrl: string,
    sceneId: string,
    nodeIds: string[],
    viewSessionId?: string
  ): Promise<HypersceneNodesVisibilityView> {
    return this.viewer(apiBaseUrl).getNodesVisibility(sceneId, nodeIds, viewSessionId);
  }

  public async getSceneFrustum(
    apiBaseUrl: string,
    sceneId: string,
    request: HypersceneFrustumRequestInfo,
    signal?: AbortSignal,
    useCompact: boolean = true,
    compactFormat: ViewerFrustumResponseFormat = 'binary'
  ): Promise<HypersceneFrustumView> {
    if (!useCompact) {
      // Deprecated standard JSON route, kept only for the demo's compactFrustum=false rollback.
      const response: HsTransportResponse = await this.clients(apiBaseUrl).transport.request({
        path: `api/Scenes/${encodeURIComponent(sceneId)}/Frustum`,
        method: 'POST',
        accept: ['application/json'],
        body: request,
        signal,
      });
      return (await hsEnsureOk(response)).json<HypersceneFrustumView>();
    }
    return this.viewer(apiBaseUrl).getFrustum(sceneId, request, {
      accept: [compactFormat === 'binary' ? HS_FRUSTUM_V4_CONTENT_TYPE : HS_FRUSTUM_JSON_CONTENT_TYPE],
      signal,
    });
  }

  public async getSceneAttributeValues(apiBaseUrl: string, sceneId: string, attributeName: string): Promise<string[]> {
    return this.viewer(apiBaseUrl).getAttributeValues(sceneId, attributeName);
  }

  public async filterSceneNodesByAttribute(
    apiBaseUrl: string,
    sceneId: string,
    attributeName: string,
    attributeValue: string,
    take: number,
    skip: number
  ): Promise<HypersceneSceneNodeFilterResultView[]> {
    return this.viewer(apiBaseUrl).filterNodesByAttribute(sceneId, attributeName, attributeValue, { take, skip });
  }

  private viewer(apiBaseUrl: string): HsViewerApi {
    return this.clients(apiBaseUrl).viewer;
  }

  private catalog(apiBaseUrl: string): HsCatalogApi {
    return this.clients(apiBaseUrl).catalog;
  }

  private ingest(apiBaseUrl: string): HsIngestApi {
    return this.clients(apiBaseUrl).ingest;
  }

  private clients(apiBaseUrl: string): ApiClients {
    const baseUrl: string = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    let clients: ApiClients | undefined = this.apiClients.get(baseUrl);
    if (clients === undefined) {
      const transport: HsHttpTransport = new HsHttpTransport({ baseUrl });
      clients = {
        transport,
        viewer: new HsViewerApi({ transport, formats: this.formats }),
        catalog: new HsCatalogApi({ transport }),
        ingest: new HsIngestApi({ transport }),
      };
      this.apiClients.set(baseUrl, clients);
    }
    return clients;
  }
}

function makeViewerFormats(): HsFormatRegistry {
  const formats: HsFormatRegistry = new HsFormatRegistry();
  formats.register(new HsFrustumFormat());
  formats.register(new HsMaterialsFormat());
  return formats;
}
